import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiPrinter } from 'react-icons/fi';
import { transactionService } from '../../services/transactionService';
import { workOrderService } from '../../services/workOrderService';
import { customerService } from '../../services/customerService';
import { pdfService } from '../../services/pdfService';

const TransactionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [transaction, setTransaction] = useState(null);
  const [workOrder, setWorkOrder] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const tx = await transactionService.getById(id);
      if (!tx) {
        setError('Transaksi tidak ditemukan');
        setLoading(false);
        return;
      }
      setTransaction(tx);

      if (tx.workOrderId) {
        const wo = await workOrderService.getById(tx.workOrderId);
        setWorkOrder(wo);
      }

      if (tx.customerId) {
        const cust = await customerService.getById(tx.customerId);
        setCustomer(cust);
      }
    } catch (error) {
      console.error('Error loading transaction:', error);
      setError('Gagal memuat data transaksi');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };
  
  const formatNumber = (amount) => {
  return new Intl.NumberFormat('id-ID', {
    minimumFractionDigits: 0
  }).format(amount || 0);
};

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('id-ID', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const getPaymentStatusColor = (status) => {
    const colors = {
      'paid': 'bg-green-100 text-green-800',
      'pending': 'bg-yellow-100 text-yellow-800',
      'failed': 'bg-red-100 text-red-800',
      'refunded': 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getPaymentStatusLabel = (status) => {
    const labels = {
      'paid': 'Lunas',
      'pending': 'Pending',
      'failed': 'Gagal',
      'refunded': 'Dikembalikan'
    };
    return labels[status] || status;
  };

  const getPaymentMethodLabel = (method) => {
    const labels = {
      'cash': 'Tunai',
      'transfer': 'Transfer Bank',
      'qris': 'QRIS',
      'credit': 'Kredit'
    };
    return labels[method] || method;
  };

  const getWOStatusLabel = (status) => {
    const labels = {
      'pending': 'Order Masuk',
      'in-progress': 'In Progress',
      'completed': 'Selesai',
      'delivered': 'Dikirim',
      'cancelled': 'Dibatalkan'
    };
    return labels[status] || status;
  };

  const getWOStatusColor = (status) => {
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'in-progress': 'bg-blue-100 text-blue-800',
      'completed': 'bg-green-100 text-green-800',
      'delivered': 'bg-purple-100 text-purple-800',
      'cancelled': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const handlePrint = () => {
    window.print();
  };

  const handleBack = () => {
    navigate('/transactions');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-[var(--text-secondary)]">Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  if (!transaction) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-[var(--text-secondary)]">Transaksi tidak ditemukan</p>
      </div>
    );
  }

  return (
    <div>
      {/* HEADER */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={handleBack}
            className="p-2 hover:bg-[var(--bg-secondary)] rounded-lg transition-colors"
          >
            <FiArrowLeft size={24} className="text-[var(--text-primary)]" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-[var(--text-primary)]">
              Detail Transaksi
            </h1>
            <p className="text-[var(--text-secondary)]">{transaction.id}</p>
          </div>
        </div>
        <button
  onClick={handlePrint}
  className="flex items-center space-x-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
>
  <FiPrinter size={18} />
  <span>Print</span>
</button>
      </div>

      {/* MAIN CONTENT */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* KOLOM KIRI (2/3) */}
        <div className="lg:col-span-2 space-y-4">
          {/* Info Transaksi */}
          <div className="bg-[var(--bg-primary)] p-6 rounded-xl shadow-[var(--shadow-sm)] border border-[var(--border-color)]">
            <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-4">
              Informasi Transaksi
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <p className="text-xs text-[var(--text-secondary)]">ID Transaksi</p>
                <p className="text-sm font-medium text-[var(--text-primary)]">{transaction.id}</p>
              </div>
              <div>
                <p className="text-xs text-[var(--text-secondary)]">WO Number</p>
                <p className="text-sm font-medium text-[var(--text-primary)]">
                  {transaction.workOrderId || '-'}
                </p>
              </div>
              <div>
                <p className="text-xs text-[var(--text-secondary)]">Tanggal</p>
                <p className="text-sm font-medium text-[var(--text-primary)]">
                  {formatDate(transaction.date)}
                </p>
              </div>
              <div>
                <p className="text-xs text-[var(--text-secondary)]">Metode Pembayaran</p>
                <p className="text-sm font-medium text-[var(--text-primary)]">
                  {getPaymentMethodLabel(transaction.paymentMethod)}
                </p>
              </div>
              <div>
                <p className="text-xs text-[var(--text-secondary)]">Status Pembayaran</p>
                <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getPaymentStatusColor(transaction.paymentStatus)}`}>
                  {getPaymentStatusLabel(transaction.paymentStatus)}
                </span>
              </div>
              {workOrder && (
                <div>
                  <p className="text-xs text-[var(--text-secondary)]">Status Work Order</p>
                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getWOStatusColor(workOrder.status)}`}>
                    {getWOStatusLabel(workOrder.status)}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Items */}
          <div className="bg-[var(--bg-primary)] p-6 rounded-xl shadow-[var(--shadow-sm)] border border-[var(--border-color)]">
            <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-4">
              Item Transaksi
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[var(--bg-secondary)]">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-[var(--text-secondary)]">Item</th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-[var(--text-secondary)]">Qty</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-[var(--text-secondary)]">Harga</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-[var(--text-secondary)]">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {transaction.items && transaction.items.length > 0 ? (
                    transaction.items.map((item, index) => (
                      <tr key={index} className="border-t border-[var(--border-color)]">
                        <td className="px-4 py-2 text-[var(--text-primary)]">{item.name}</td>
                        <td className="px-4 py-2 text-center text-[var(--text-primary)]">{item.quantity}</td>
                        <td className="px-4 py-2 text-right text-[var(--text-primary)]">{formatNumber(item.price)}</td>
                        <td className="px-4 py-2 text-right font-semibold text-[var(--text-primary)]">{formatNumber(item.total)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="px-4 py-4 text-center text-[var(--text-secondary)]">
                        Tidak ada item
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Notes */}
          {transaction.notes && (
            <div className="bg-[var(--bg-primary)] p-6 rounded-xl shadow-[var(--shadow-sm)] border border-[var(--border-color)]">
              <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-2">
                Catatan
              </h3>
              <p className="text-sm text-[var(--text-primary)]">{transaction.notes}</p>
            </div>
          )}
        </div>

        {/* KOLOM KANAN (1/3) - SUMMARY */}
        <div className="lg:col-span-1">
          <div className="bg-[var(--bg-primary)] p-6 rounded-xl shadow-[var(--shadow-sm)] border border-[var(--border-color)] sticky top-4">
            <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-4">
              Ringkasan
            </h3>

            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-[var(--text-secondary)]">Subtotal</span>
                <span className="text-[var(--text-primary)]">{formatNumber(transaction.subtotal || 0)}</span>
              </div>

              {(transaction.discount || 0) > 0 && (
                <div className="flex justify-between text-red-600">
                  <span>Diskon {transaction.discountPercent || 0}%</span>
                  <span>- {formatNumber(transaction.discount || 0)}</span>
                </div>
              )}

              {(transaction.markup || 0) > 0 && (
                <div className="flex justify-between text-blue-600">
                  <span>Biaya Penyesuaian {transaction.markupPercent || 0}%</span>
                  <span>+ {formatNumber(transaction.markup || 0)}</span>
                </div>
              )}

              <div className="flex justify-between text-sm border-t border-[var(--border-color)] pt-2 mt-2">
                <span className="text-[var(--text-secondary)]">After Adjustment</span>
                <span className="text-[var(--text-primary)]">
                  {formatCurrency((transaction.subtotal || 0) + (transaction.markup || 0) - (transaction.discount || 0))}
                </span>
              </div>

              <div className="flex justify-between text-purple-600">
                <span>{transaction.taxName || 'PPN'} {(transaction.taxPercent || 0)}%</span>
                <span>+ {formatNumber(transaction.tax || 0)}</span>
              </div>

              <div className="flex justify-between font-bold pt-3 mt-3 border-t-2 border-[var(--border-color)]">
                <span className="text-base text-[var(--text-primary)]">Total</span>
                <span className="text-base text-[var(--text-primary)]">{formatCurrency(transaction.total || 0)}</span>
              </div>
            </div>

            {/* Customer Info */}
            {customer && (
              <div className="mt-4 pt-4 border-t border-[var(--border-color)]">
                <h4 className="text-sm font-semibold text-[var(--text-primary)] mb-2">Customer</h4>
                <p className="text-sm text-[var(--text-primary)]">{customer.name}</p>
                <p className="text-xs text-[var(--text-secondary)]">{customer.phone}</p>
                <p className="text-xs text-[var(--text-secondary)]">{customer.email}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransactionDetail;